/**
 * Watch History Service
 * Tracks user video watching progress and provides "Continue Watching" functionality
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Debounce utility
let updateTimeout = null;

/**
 * Get auth headers if user is logged in
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('userToken');
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
};

/**
 * Update watch progress for a video
 * @param {Object} params
 * @param {string} params.content_type - 'live', 'vod', or 'recording'
 * @param {string} params.content_id - ID of the content
 * @param {string} params.title - Title of the video
 * @param {string} params.thumbnail - Thumbnail URL
 * @param {number} params.duration - Total duration in seconds
 * @param {number} params.progress - Current position in seconds
 * @param {string} params.channel_id - Optional channel ID
 */
export const updateWatchProgress = async ({
  content_type,
  content_id,
  title,
  thumbnail,
  duration,
  progress,
  channel_id
}) => {
  const headers = getAuthHeaders();
  if (!headers) return; // Don't track for non-logged-in users

  // Debounce updates to avoid too many API calls
  if (updateTimeout) {
    clearTimeout(updateTimeout);
  }

  updateTimeout = setTimeout(async () => {
    try {
      await axios.post(`${API}/users/preferences/watch-history`, {
        content_type,
        content_id,
        title,
        thumbnail,
        duration: Math.floor(duration),
        progress: Math.floor(progress),
        channel_id
      }, { headers });
    } catch (err) {
      console.error('Failed to update watch history:', err);
    }
  }, 2000); // Debounce for 2 seconds
};

/**
 * Get user's watch history
 * @param {number} limit - Maximum number of entries to return
 * @param {string} content_type - Optional filter by content type
 */
export const getWatchHistory = async (limit = 20, content_type = null) => {
  const headers = getAuthHeaders();
  if (!headers) return [];

  try {
    const params = { limit };
    if (content_type) params.content_type = content_type;
    
    const res = await axios.get(`${API}/users/preferences/watch-history`, {
      headers,
      params
    });
    return res.data.history || [];
  } catch (err) {
    console.error('Failed to get watch history:', err);
    return [];
  }
};

/**
 * Get videos user started but didn't finish
 * @param {number} limit - Maximum number of entries to return
 */
export const getContinueWatching = async (limit = 10) => {
  const headers = getAuthHeaders();
  if (!headers) return [];

  try {
    const res = await axios.get(`${API}/users/preferences/continue-watching`, {
      headers,
      params: { limit }
    });
    return res.data.continue_watching || [];
  } catch (err) {
    console.error('Failed to get continue watching:', err);
    return [];
  }
};

/**
 * Delete a watch history entry
 * @param {string} content_id - ID of the content to remove
 */
export const deleteWatchHistoryEntry = async (content_id) => {
  const headers = getAuthHeaders();
  if (!headers) return false;

  try {
    await axios.delete(`${API}/users/preferences/watch-history/${content_id}`, { headers });
    return true;
  } catch (err) {
    console.error('Failed to delete watch history entry:', err);
    return false;
  }
};

/**
 * Clear all watch history
 */
export const clearWatchHistory = async () => {
  const headers = getAuthHeaders();
  if (!headers) return false;

  try {
    await axios.delete(`${API}/users/preferences/watch-history`, { headers });
    return true;
  } catch (err) {
    console.error('Failed to clear watch history:', err);
    return false;
  }
};
